import { Divider, Grid, Typography } from '@mui/material';
import { ActionButton } from '@tassat/button';
import { ActionTypes, doesWorkflowRejectCodeExist } from '@tassat/common-definitions';
import { AccountCreationForm as AccountForm } from '@tassat/forms';
import { AccountRecord, CustomerModifyFirmAccountReq } from '@tassat/fred-definitions';
import { Edit, Lock, Unlock } from '@tassat/icon';
import { FormModal } from '@tassat/modal';
import { AttributeBadge, AttributeTag, ClosedSymbol } from '@tassat/symbols';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React from 'react';

import {
    getTransactions, submitAccountForm, updateFirmAccount, validateDdaAccount
} from 'Containers/admin/forms/helpers/accountFormFn';
import { LOCK_ICON_COLOR } from 'Lib/constant';
import { formatEpochDate, obscureAccountNo } from 'Lib/stringHelpers';
import Worker from 'Lib/worker/PrimusWorkerWrapper';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAssociatedWalletsMappedbyAccount } from 'Selectors/accountRelationships';
import { getAllAccountsArray, getAllAccountsByFirmId } from 'Selectors/accounts';
import { getCurrentRole } from 'Selectors/common';

import type { FirmAccountDetailsHeaderProps } from '.';

export function FirmAccountDetailsHeader (props: FirmAccountDetailsHeaderProps) {
    const {
        classes,
        firm,
        mappedAccount
    } = props;

    const userRole = useAppSelector(getCurrentRole);
    const firmAccounts = useAppSelector((state) => getAllAccountsByFirmId(state, firm?.FirmId));
    const allAccounts = useAppSelector(getAllAccountsArray);
    const { account } = mappedAccount;
    const associatedWallets = useAppSelector((state) => getAssociatedWalletsMappedbyAccount(state, mappedAccount?.account?.AccountId));

    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] =
    React.useReducer(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);
    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);
    const [ editAccountModalOpen, setEditAccountModalOpen ] = React.useState<boolean>(false);
    const closeEditAccountModal = () => setEditAccountModalOpen(false);

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                selectedItem: account,
                setSelectedItem: () => account,
                preActionValidationAndConfig: {
                    [ActionTypes.lock]: lockAccountValidation,
                    [ActionTypes.unlock]: lockAccountValidation
                },
                onConfirm: {
                    [ActionTypes.lock]: lockAccount,
                    [ActionTypes.unlock]: lockAccount
                }
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    if (!account) return <div />;

    const lockAccountValidation = (account: AccountRecord): boolean => {
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: account,
                actionType: account.IsLocked ? ActionTypes.unlock : ActionTypes.lock,
                confirmTitle: `Confirm ${account.IsLocked ? 'Unlocking' : 'Locking'} BD Account and Any Linked Accounts`,
                confirmBody: `Please confirm ${account.IsLocked ? 'unlocking' : 'locking'} user functionality for ${account.AccountName}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const lockAccount = async (account: AccountRecord) => {
        try {
            const accountUpdateValues: Partial<CustomerModifyFirmAccountReq> = { IsLocked: !account.IsLocked };

            const response = await Worker.getInstance()
                .updateFirmAccount(account.FirmId, account.AccountId, accountUpdateValues);

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully ${account.IsLocked ? 'Unlocked' : 'Locked'} BD Account and Any Linked Accounts`,
                        successBody: `Successfully ${account.IsLocked ? 'unlocked' : 'locked'} user functionality for ${account.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to ${account.IsLocked ? 'Unlock' : 'Lock'} BD Account and Any Linked Accounts`,
                        failureBody: `Could not ${account.IsLocked ? 'unlock' : 'lock'} account functionality for ${account.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to ${account.IsLocked ? 'Unlock' : 'Lock'} BD Account and Any Linked Accounts`,
                    failureBody: `Something went wrong ${account.IsLocked ? 'unlocking' : 'locking'} account functionality for ${account.AccountName}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <Grid
                className={classes.accountHeaderContainer}
                container
                direction='row'
                justifyContent='space-around'
                spacing={0}
                wrap='wrap'
            >
                <Grid
                    container
                    direction='column'
                    item
                    spacing={0}
                    wrap='nowrap'
                    xs={4}
                >
                    <Grid
                        container
                        direction='row'
                        spacing={0}
                    >
                        <Typography
                            className={classes.accountNumberDetails}
                            variant='h5'
                        >{obscureAccountNo(account.Account)}
                        </Typography>
                    </Grid>
                    <Typography
                        className={classes.accountName}
                        variant='h4'
                    >{account.AccountName}
                    </Typography>
                    <Grid
                        alignItems='center'
                        container
                        direction='row'
                        spacing={0}
                        wrap='nowrap'
                    >
                        <AttributeBadge
                            active
                            className={classes.statusSymbol}
                            value={account.AccessControl ? 'Y' : 'N'}
                        />
                        {account.IsWire &&
                            <AttributeTag
                                attribute='Wire'
                                className={classes.statusSymbol}
                            />}
                        {account.IsLocked &&
                            <Lock
                                className={classes.statusSymbol}
                                iconColor={LOCK_ICON_COLOR}
                            />}
                    </Grid>
                    <Typography
                        className={classes.accountNotifyEmail}
                        variant='body2'
                    >{account.Email}
                    </Typography>
                </Grid>
                <Grid
                    container
                    direction='column'
                    item
                    spacing={0}
                    wrap='nowrap'
                    xs={4}
                >
                    <Grid
                        container
                        direction='row'
                        justifyContent='space-between'
                        spacing={0}
                        wrap='nowrap'
                    />
                </Grid>
                <Divider
                    className={classes.verticalDivider}
                    orientation='vertical'
                />
                <Grid
                    container
                    direction='column'
                    item
                    spacing={0}
                    wrap='nowrap'
                    xs={2}
                >
                    <Typography
                        className={classes.detailLabel}
                        variant='body2'
                    >Notes
                    </Typography>
                    {account.Notes ?
                        <Typography
                            className={classes.accountDetail}
                            variant='body1'
                        >{account.Notes}
                        </Typography>
                        : (
                            <Typography
                                className={`${classes.accountDetail} ${classes.noNotes}`}
                                variant='body1'
                            >No Notes
                            </Typography>
                        )}
                    <Typography
                        className={classes.accountTimeDetail}
                        variant='body1'
                    >Created {account.CreatedAt && account.CreatedAt !== '0' ? formatEpochDate(account.CreatedAt) : '-'}
                    </Typography>
                    {account.IsClosed && account.ClosedAt && account.ClosedAt !== '0' &&
                        <Typography
                            className={classes.accountTimeDetail}
                            variant='body1'
                        >Closed {formatEpochDate(account.ClosedAt)}
                        </Typography>}
                    {!account.IsClosed && account.ReopenAt && account.ReopenAt !== '0' &&
                        <Typography
                            className={classes.accountTimeDetail}
                            variant='body1'
                        >Reopened {formatEpochDate(account.ReopenAt)}
                        </Typography>}
                </Grid>
                <Grid
                    alignItems='flex-end'
                    container
                    direction='column'
                    item
                    justifyContent='space-between'
                    spacing={0}
                    wrap='nowrap'
                    xs={1}
                >
                    <Grid
                        alignItems='center'
                        className={classes.actionButtonsContainer}
                        container
                        direction='row'
                        item
                        justifyContent='flex-end'
                        spacing={0}
                        wrap='nowrap'
                        xs={12}
                    >
                        {account.IsClosed &&
                            <ClosedSymbol
                                className={classes.closedTag}
                            />}
                        {userRole.IsManageFirmAllowed &&
                            <ActionButton
                                disabled={account.IsClosed || firm?.IsClosed}
                                handleClick={() => lockAccountValidation(account)}
                                Icon={account.IsLocked ? Unlock : Lock}
                                text={account.IsLocked ? 'Unlock' : 'Lock'}
                                type='icon'
                            />}
                    </Grid>
                    {userRole.IsManageFirmAllowed &&
                        <ActionButton
                            disabled={firm?.IsClosed}
                            handleClick={() => setEditAccountModalOpen(true)}
                            Icon={Edit}
                            text='Edit'
                            type='icon'
                        />}
                </Grid>
            </Grid>
            <FormModal
                Form={AccountForm}
                formProps={{
                    firm: firm,
                    actionType: ActionTypes.edit,
                    editAccount: account,
                    firmAccounts,
                    submitAccountForm,
                    validateDdaAccount,
                    updateFirmAccount,
                    getTransactions,
                    associatedWallets,
                    allAccounts
                }}
                modalWidth='744px'
                onClose={closeEditAccountModal}
                open={editAccountModalOpen}
                title='Edit BD Account'
            />
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
}